import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, MessageSquare } from "lucide-react";
import type { Document } from "@/types/documents";
import { downloadDocument } from "@/stores/documents/repository";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { PDFRenderer } from "./PDFRenderer";
import { usePdfDialogScroll, type PdfFocusReturnTarget } from "./pdf/usePdfDialogScroll";
import { PdfRendererBoundary } from "./pdf/PdfRendererBoundary";
import { DocxRenderer } from "./DocxRenderer";
import { XlsxRenderer, type XlsxRendererHandle } from "./XlsxRenderer";
import { isWordFile, isExcelFile } from "@/utils/previewUtils";
import { isIfcFile } from "@/domains/documents/constants/fileTypes";
import { IfcPreviewDialog } from "./IfcPreviewDialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import { useResizablePanelWidth } from "@/hooks/useResizablePanelWidth";
import { useBottomRightExclusion } from "@/components/layout/useBottomRightExclusion";
import { cn } from "@/lib/utils";
import { useOptionalStores } from "@/stores/useStores";
import { DocumentCommentsThread } from "./comments/DocumentCommentsThread";
import { useDocumentOpenCommentCount } from "./comments/useDocumentOpenCommentCount";
import type { PdfPagesRendererHandle } from "./pdf/types";

/** Per-viewer preference: whether the comments rail is shown. */
const RAIL_OPEN_KEY = "document-comments-rail-open";
const RAIL_STORAGE_KEY = "document-comments-rail";

function readRailOpen(): boolean {
  try {
    return localStorage.getItem(RAIL_OPEN_KEY) !== "0";
  } catch {
    return true;
  }
}

function storeRailOpen(open: boolean): void {
  try {
    localStorage.setItem(RAIL_OPEN_KEY, open ? "1" : "0");
  } catch {
    // Per-viewer convenience only.
  }
}
import { track } from "@/lib/posthog";
import { fileExtProp } from "@/lib/analytics/eventProps";

interface DocumentViewerDialogProps {
  document: Document | null;
  /** Pre-loaded blob URL (required - loading happens in parent) */
  blobUrl: string | null;
  /** Raw blob data for Office file rendering (docx, xlsx). Optional — only needed for Office files. */
  blobData?: Blob | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Capture before asynchronous loading can remount the activating control. */
  focusReturnTarget?: HTMLElement | PdfFocusReturnTarget | null;
  /** Original file path for DWG files (since document.file_path points to child PDF for preview) */
  originalDwgFilePath?: string;
  /** Override the default download handler (for public share pages that don't use Supabase auth) */
  onDownloadOverride?: () => void;
  /** Extra classes for the dialog content + backdrop — lets the 3D viewer
   * elevate it above the fullscreen preview overlay (WHA-3729 follow-up). */
  contentClassName?: string;
  overlayClassName?: string;
  /**
   * The comments rail beside the file. Default on; the public share page
   * passes false (no session there anyway, but the toggle must not show).
   */
  commentsEnabled?: boolean;
  /** Deep link: open the rail on this thread. */
  commentsInitialThreadId?: number | null;
  /** Force the rail open for this document (deep links, "go to page"). */
  commentsDefaultOpen?: boolean;
  /** 1-based page to open a PDF on (a document comment or a brief suggestion's
   * cited page); the native-viewer fallback honours it too. */
  initialPage?: number;
}

export function DocumentViewerDialog({
  document,
  blobUrl,
  blobData,
  open,
  onOpenChange,
  focusReturnTarget,
  originalDwgFilePath,
  onDownloadOverride,
  contentClassName,
  overlayClassName,
  commentsEnabled = true,
  commentsInitialThreadId = null,
  commentsDefaultOpen = false,
  initialPage,
}: DocumentViewerDialogProps) {
  const { dialogRef, onOpenAutoFocus, onCloseAutoFocus, onKeyDown, focusPreview } =
    usePdfDialogScroll(
      Boolean(document?.content_type?.startsWith("application/pdf")),
      focusReturnTarget
    );
  const { t } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  // Matches the PermissionProtectedRoute on the Drawings routes. Still false
  // for anonymous viewers — usePermissions returns an all-false object when
  // there is no organization or user — so public share pages never offer the
  // jump to a route that would bounce them.
  const { canViewDrawingCompareTool } = usePermissions();
  const isDwgFile = document?.filename?.toLowerCase().endsWith(".dwg");
  const isIfcDocument = isIfcFile(document?.filename);
  const reportedViewRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) reportedViewRef.current = null;
  }, [open]);

  const reportDocumentView = useCallback(
    (viewKey: string, surface: "document_viewer" | "ifc_preview") => {
      if (!open || !document) return;
      if (reportedViewRef.current === viewKey) return;
      reportedViewRef.current = viewKey;
      track("document_viewed", {
        document_id: String(document.id),
        file_name: document.filename,
        viewer: "document",
        surface,
      });
    },
    [document, open]
  );

  const handleViewReady = useCallback(() => {
    if (!document || !blobUrl) return;
    const viewKey = `document:${document.id}:${blobUrl}`;
    reportDocumentView(viewKey, "document_viewer");
  }, [blobUrl, document, reportDocumentView]);

  const handleIfcPreviewReady = useCallback(() => {
    if (!document) return;
    const viewKey = `ifc-preview:${document.id}`;
    reportDocumentView(viewKey, "ifc_preview");
  }, [document, reportDocumentView]);

  // --- comments rail ---------------------------------------------------------
  const stores = useOptionalStores();
  const userProfile = stores?.userStore.userProfile ?? null;
  const docIdNum = document ? Number(document.id) : Number.NaN;
  const buildingIdNum = document ? Number(document.building_id) : Number.NaN;
  // Never for an anonymous viewer (the share page) and never for an IFC, which
  // opens its own confirm modal rather than this shell.
  const railEligible =
    commentsEnabled &&
    Boolean(userProfile) &&
    Number.isFinite(docIdNum) &&
    Number.isFinite(buildingIdNum) &&
    !isIfcDocument;
  const [railOpen, setRailOpen] = useState(readRailOpen);
  useEffect(() => {
    if (commentsDefaultOpen) setRailOpen(true);
  }, [commentsDefaultOpen, document?.id]);
  const toggleRail = () =>
    setRailOpen((current) => {
      storeRailOpen(!current);
      return !current;
    });
  // What the pdf.js renderer reports; reset per document so a card's page
  // badge never scrolls a renderer that belongs to the previous file.
  const [currentPage, setCurrentPage] = useState<number | null>(null);
  const [paged, setPaged] = useState(false);
  // A spreadsheet's anchor is a sheet, not a page: same number slot, its own
  // name and its own way of getting there.
  const [sheetName, setSheetName] = useState<string | null>(null);
  useEffect(() => {
    setCurrentPage(null);
    setPaged(false);
    setSheetName(null);
  }, [document?.id]);
  const rendererRef = useRef<PdfPagesRendererHandle>(null);
  const xlsxRef = useRef<XlsxRendererHandle>(null);
  const rail = useResizablePanelWidth(RAIL_STORAGE_KEY, 380, {
    minWidth: 300,
    anchor: "right",
    maxWidth: typeof window !== "undefined" ? window.innerWidth * 0.5 : 600,
  });
  const railVisible = open && railEligible && railOpen;
  useBottomRightExclusion(RAIL_STORAGE_KEY, rail.settledWidth, railVisible);
  const openCount = useDocumentOpenCommentCount(railEligible ? docIdNum : null);

  // This preview renders the DWG's flattened PDF rendition — offer the jump to
  // the real vector drawing in the Drawings tool.
  const openDrawingViewer =
    isDwgFile && canViewDrawingCompareTool && document?.id && document?.building_id
      ? () => {
          onOpenChange(false);
          navigate(`/buildings/${document.building_id}/drawings?ids=${document.id}`);
        }
      : undefined;

  const handleDownloadClick = async () => {
    if (!document) return;

    try {
      // For DWG files, use the original file path (not the child PDF path)
      if (isDwgFile && originalDwgFilePath) {
        // Download the original DWG file via blob for proper filename handling
        const { data, error } = await supabase.storage
          .from("documents")
          .download(originalDwgFilePath);

        if (error) throw new Error(error.message);
        if (!data) throw new Error("Failed to download file");

        // Create blob URL and trigger download with proper filename
        // Wrap in try-finally to ensure cleanup even if an error occurs
        const dwgBlobUrl = URL.createObjectURL(data);
        try {
          const link = window.document.createElement("a");
          link.href = dwgBlobUrl;
          link.download = document.filename;
          window.document.body.appendChild(link);
          link.click();
          window.document.body.removeChild(link);
        } finally {
          URL.revokeObjectURL(dwgBlobUrl);
        }
        track("document_downloaded", {
          file_ext: fileExtProp(document.filename),
          surface: "viewer",
        });

        toast({
          title: "Download Started",
          description: `Downloading ${document.filename}`,
        });
      } else {
        // For non-DWG files, use the standard download (handles download internally)
        await downloadDocument(document.id, document.filename, { surface: "viewer" });

        toast({
          title: "Download Started",
          description: `Downloading ${document.filename}`,
        });
      }
    } catch (error) {
      console.error("Download error:", error);
      toast({
        title: "Download Failed",
        description: "Could not download the file. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleOpenNewTab = () => {
    if (blobUrl) {
      window.open(blobUrl, "_blank");
    }
  };

  const renderContent = () => {
    if (!document) return null;

    const filename = document.filename;

    if (!blobUrl) return null;

    // Word documents — render via docx-preview
    if (blobData && isWordFile(filename)) {
      return (
        <DocxRenderer
          blobData={blobData}
          filename={filename}
          onDownload={onDownloadOverride ?? handleDownloadClick}
          onLoad={handleViewReady}
        />
      );
    }

    // Excel spreadsheets — render via SheetJS
    if (blobData && isExcelFile(filename)) {
      return (
        <XlsxRenderer
          key={document.id}
          ref={xlsxRef}
          blobData={blobData}
          filename={filename}
          onDownload={onDownloadOverride ?? handleDownloadClick}
          onLoad={handleViewReady}
          initialSheet={initialPage}
          onActiveSheetChange={(sheet) => {
            setCurrentPage(sheet.index);
            setSheetName(sheet.name);
            setPaged(true);
          }}
        />
      );
    }

    if (document.content_type?.startsWith("image/")) {
      // Bound BOTH axes: the wrapper below is overflow-hidden with no scroll, so
      // an unconstrained width silently clips wide scans/plans out of reach.
      return (
        <img
          src={blobUrl}
          alt={filename}
          className="mx-auto max-h-full max-w-full"
          onLoad={handleViewReady}
        />
      );
    }

    if (document.content_type?.startsWith("application/pdf")) {
      const nativeViewer = (
        <PDFRenderer
          docId={document.id}
          blobUrl={blobUrl}
          onOpenInTab={handleOpenNewTab}
          onDownload={onDownloadOverride ?? handleDownloadClick}
          isDwgFile={isDwgFile}
          onOpenDrawingViewer={openDrawingViewer}
          filename={document.filename}
          className="h-full"
          onLoad={handleViewReady}
          initialPage={initialPage}
          keyboardScroll
        />
      );
      // pdf.js page renderer when we hold the bytes; the native viewer is the
      // fallback when pdf.js cannot load or parse them, and the only option
      // when the parent handed us a URL alone.
      if (!blobData) return nativeViewer;
      return (
        <PdfRendererBoundary
          key={document.id}
          ref={rendererRef}
          blob={blobData}
          docId={document.id}
          initialPage={initialPage}
          onCurrentPageChange={setCurrentPage}
          onPageCountChange={() => {
            focusPreview();
            // pdf.js has parsed the file: the document counts as viewed.
            handleViewReady();
            setPaged(true);
            // The observer only reports on scroll; before the reader moves, the
            // composer would otherwise offer no page chip at all.
            setCurrentPage((page) => page ?? 1);
          }}
          onFallback={() => setPaged(false)}
          onOpenInTab={handleOpenNewTab}
          onDownload={onDownloadOverride ?? handleDownloadClick}
          isDwgFile={isDwgFile}
          onOpenDrawingViewer={openDrawingViewer}
          fallback={nativeViewer}
          className="h-full"
        />
      );
    }

    return (
      <iframe src={blobUrl} className="h-full w-full" title={filename} onLoad={handleViewReady} />
    );
  };

  // IFC (BIM) — WHA-3685: show a still screenshot of the 3D structure (rendered
  // by the pipeline at the /models iso angle) with Close / Open 3D model. We
  // still don't embed the live scene (WHA-3604) — the modal displays the
  // pre-rendered thumbnail and deep-links into the full viewer on confirm.
  if (isIfcDocument && document) {
    return (
      <IfcPreviewDialog
        document={document}
        open={open}
        onOpenChange={onOpenChange}
        onPreviewLoad={handleIfcPreviewReady}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName={overlayClassName}
        // Size and centre against the visible viewport so mobile browser chrome
        // cannot clip the viewer. Keep a clear inset so the large viewer still
        // reads as a modal over the Documents page rather than a page takeover.
        className={`top-[50dvh] flex h-[calc(100dvh-3rem)] max-h-none w-[calc(100vw-3rem)] max-w-none flex-col overflow-hidden rounded-lg ${contentClassName ?? ""}`}
        ref={dialogRef}
        onOpenAutoFocus={onOpenAutoFocus}
        onCloseAutoFocus={onCloseAutoFocus}
        onKeyDown={onKeyDown}
      >
        {document && (
          <DialogHeader className="shrink-0 border-b pb-4">
            <div className="flex items-center justify-between">
              <div className="flex min-w-0 flex-1 items-center gap-3 pr-4">
                <DialogTitle className="truncate text-base font-medium">
                  {document.filename}
                </DialogTitle>
                {isDwgFile && (
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <div className="flex shrink-0 cursor-help items-center gap-1.5 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
                          <AlertTriangle className="h-3.5 w-3.5" />
                          <span className="font-medium">Caution</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-sm text-sm">
                        <p>
                          This PDF is generated from a DWG file and is provided for visual purposes.
                          It may not fully match the original model space and should not be used as
                          an official or contractual drawing.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              {railEligible && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  // Pressed reads as a red outline, like every other highlight on
                  // the comments surfaces — never a grey fill. The transparent
                  // border at rest keeps the label from shifting when it toggles.
                  className={cn(
                    "mr-8 h-7 shrink-0 border text-xs",
                    railOpen
                      ? "border-red-600 text-red-600 hover:bg-transparent hover:text-red-700"
                      : "border-transparent"
                  )}
                  aria-pressed={railOpen}
                  onClick={toggleRail}
                >
                  <MessageSquare className="mr-1 h-3.5 w-3.5" />
                  {t.documents.comments.toggleRail}
                  {openCount > 0 && (
                    <span className="ml-1 rounded border border-red-600 px-1 text-[10px] font-medium tabular-nums text-red-600">
                      {openCount}
                    </span>
                  )}
                </Button>
              )}
            </div>
          </DialogHeader>
        )}
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <div className="min-h-0 min-w-0 flex-1 overflow-hidden">{renderContent()}</div>
          {railVisible && (
            <aside
              aria-label={t.documents.comments.title}
              className="relative flex min-h-0 shrink-0 flex-col border-l bg-background"
              style={{ width: rail.width }}
            >
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize panel"
                {...rail.handleProps}
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 6,
                  cursor: "col-resize",
                  zIndex: 2,
                  touchAction: "none",
                  background:
                    rail.dragging || rail.hover ? "hsl(var(--primary) / 0.4)" : "transparent",
                  transition: "background 120ms",
                }}
              />
              <DocumentCommentsThread
                key={docIdNum}
                documentId={docIdNum}
                buildingId={buildingIdNum}
                currentPage={currentPage}
                pagedRenderer={paged}
                anchorKind={sheetName != null ? "sheet" : "page"}
                anchorName={sheetName}
                onGoToPage={(page, docId) => {
                  if (docId !== docIdNum) return;
                  if (sheetName != null) xlsxRef.current?.showSheet(page);
                  else rendererRef.current?.scrollToPage(page);
                }}
                initialThreadId={commentsInitialThreadId}
                className="min-h-0 flex-1"
              />
            </aside>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
